import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

function AccountSuspended() {
  const navigate = useNavigate();
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchBillingStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchBillingStatus = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${process.env.REACT_APP_API_URL}/admin/billing-status`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setBilling(data);

        // If not actually suspended, redirect to dashboard
        if (!data.isSuspended) {
          navigate('/admin/dashboard');
        }
      } else {
        setError('Failed to load billing information');
      }
    } catch (err) {
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin/login');
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, var(--color-error-bg) 0%, var(--color-error-border) 100%)'
      }}>
        <div className="loading-spinner"></div>
      </div>
    );
  }

  const tierNames = {
    starter: 'Starter',
    pro: 'PRO',
    enterprise: 'Enterprise'
  };

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, var(--color-error-bg) 0%, var(--color-error-border) 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '20px'
    }}>
      <div style={{
        maxWidth: '600px',
        width: '100%',
        background: 'var(--color-surface)',
        borderRadius: '20px',
        boxShadow: '0 20px 60px rgba(220, 38, 38, 0.2)',
        overflow: 'hidden'
      }}>
        {/* Header */}
        <div style={{
          background: 'linear-gradient(135deg, #dc2626 0%, #b91c1c 100%)',
          padding: '40px',
          textAlign: 'center',
          color: 'white'
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            background: 'rgba(255,255,255,0.2)',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: '0 auto 20px',
            fontSize: '40px'
          }}>
            ⚠️
          </div>
          <h1 style={{
            margin: 0,
            fontSize: '28px',
            fontWeight: '700'
          }}>
            Account Suspended
          </h1>
          <p style={{
            margin: '10px 0 0',
            opacity: 0.9,
            fontSize: '16px'
          }}>
            Your account access has been temporarily restricted
          </p>
        </div>

        {/* Content */}
        <div style={{ padding: '40px' }}>
          {error ? (
            <div style={{
              background: 'var(--color-error-bg)',
              color: 'var(--color-error-text)',
              padding: '15px',
              borderRadius: '10px',
              marginBottom: '20px'
            }}>
              {error}
            </div>
          ) : billing && (
            <>
              <p style={{
                color: 'var(--color-text-light)',
                fontSize: '16px',
                lineHeight: '1.6',
                marginBottom: '30px'
              }}>
                Your DeedStack account for <strong>{billing.companyName}</strong> has been suspended
                due to non-payment. Please settle your outstanding balance to restore access.
              </p>

              {/* Amount Due Box */}
              <div style={{
                background: 'var(--color-error-bg)',
                border: '2px solid var(--color-error-border)',
                borderRadius: '12px',
                padding: '25px',
                marginBottom: '30px'
              }}>
                <h3 style={{
                  margin: '0 0 20px',
                  color: 'var(--color-error-text)',
                  fontSize: '18px'
                }}>
                  Amount Due to Reactivate
                </h3>

                <div style={{ marginBottom: '15px' }}>
                  <div style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    marginBottom: '10px',
                    color: 'var(--color-text-light)'
                  }}>
                    <span>{tierNames[billing.subscriptionTier] || 'Starter'} Plan - Monthly</span>
                    <span>${billing.monthlyRate?.toFixed(2)}</span>
                  </div>

                  {billing.lateFee?.applied && !billing.lateFee?.waived && (
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: '10px',
                      color: 'var(--color-error)'
                    }}>
                      <span>Late Fee</span>
                      <span>${billing.lateFee.amount?.toFixed(2)}</span>
                    </div>
                  )}

                  {billing.lateFee?.waived && (
                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      marginBottom: '10px',
                      color: 'var(--color-success-text)'
                    }}>
                      <span>Late Fee</span>
                      <span style={{ textDecoration: 'line-through' }}>
                        ${billing.lateFee.amount?.toFixed(2)} (Waived)
                      </span>
                    </div>
                  )}
                </div>

                <div style={{
                  borderTop: '2px solid var(--color-error-border)',
                  paddingTop: '15px',
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: '20px',
                  fontWeight: '700'
                }}>
                  <span>Total Due</span>
                  <span style={{ color: 'var(--color-error)' }}>${billing.totalDue?.toFixed(2)}</span>
                </div>

                {billing.daysPastDue > 0 && (
                  <p style={{
                    margin: '15px 0 0',
                    fontSize: '14px',
                    color: 'var(--color-error-text)'
                  }}>
                    {billing.daysPastDue} days past due
                  </p>
                )}
              </div>

              {/* What this means */}
              <div style={{
                background: 'var(--color-surface-sunken)',
                borderRadius: '12px',
                padding: '20px',
                marginBottom: '30px'
              }}>
                <h4 style={{ margin: '0 0 15px', color: 'var(--color-text)' }}>What this means:</h4>
                <ul style={{
                  margin: 0,
                  paddingLeft: '20px',
                  color: 'var(--color-text-light)',
                  lineHeight: '1.8'
                }}>
                  <li>Your admin dashboard access is restricted</li>
                  <li>You cannot manage properties, loans, or customers</li>
                  <li>Your public property listings remain visible to buyers</li>
                  <li>Payment will instantly restore full access</li>
                </ul>
              </div>

              {/* Pay Button - navigates to billing page */}
              <button
                onClick={() => navigate('/admin/billing')}
                style={{
                  width: '100%',
                  padding: '18px',
                  borderRadius: '12px',
                  border: 'none',
                  background: 'linear-gradient(135deg, #dc2626, #b91c1c)',
                  color: 'white',
                  fontSize: '18px',
                  fontWeight: '700',
                  cursor: 'pointer',
                  marginBottom: '15px',
                  transition: 'transform 0.2s, box-shadow 0.2s'
                }}
                onMouseOver={(e) => {
                  e.target.style.transform = 'translateY(-2px)';
                  e.target.style.boxShadow = '0 10px 30px rgba(220, 38, 38, 0.3)';
                }}
                onMouseOut={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = 'none';
                }}
              >
                Pay ${billing.totalDue?.toFixed(2)} & Reactivate
              </button>

              {/* Contact Support */}
              <div style={{
                textAlign: 'center',
                color: 'var(--color-text-light)',
                fontSize: '14px'
              }}>
                <p style={{ margin: '0 0 10px' }}>
                  Need help? Contact us at{' '}
                  <a href="mailto:support@deedstack.com" style={{ color: 'var(--color-error)' }}>
                    support@deedstack.com
                  </a>
                </p>
                <button
                  onClick={handleLogout}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: 'var(--color-text-muted)',
                    cursor: 'pointer',
                    fontSize: '14px',
                    textDecoration: 'underline'
                  }}
                >
                  Logout
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default AccountSuspended;
